import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import {
    Table as TableSimple,
    type TableColumn,
    type TableAction,
} from "@/components/Table";
import { Tabs, TabItem } from "@/components/Tabs";
import { Modal } from "@/components/Modal";
import { PasswordUserGroupsView } from "@/pages/Passwords/PasswordUserGroupsView";
import { PasswordGroup } from "@/types/password";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { formatDate } from "@/utils/format";

interface User {
    id: number;
    name: string;
    email: string;
}

interface PasswordUserGroupOption {
    id: number;
    name: string;
}

interface PasswordGroupsViewProps {
    groups: PasswordGroup[];
    searchQuery: string;
    onSearchChange: (value: string) => void;
    onEditGroup: (group: PasswordGroup) => void;
    onDeleteGroup: (group: PasswordGroup) => void;
    users: User[];
    subTab: "password_groups" | "user_groups";
    onChangeSubTab: (tab: "password_groups" | "user_groups") => void;
    triggerCreateUserGroup?: number;
}

type GroupsSubTab = "password_groups" | "user_groups";

export function PasswordGroupsView({
    groups,
    searchQuery,
    onSearchChange,
    onEditGroup,
    onDeleteGroup,
    users,
    subTab,
    onChangeSubTab,
    triggerCreateUserGroup,
}: PasswordGroupsViewProps) {
    const { t } = useTranslation("global");
    const { user } = useAuth();
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<PasswordGroup | null>(
        null
    );

    const openDetails = (group: PasswordGroup) => {
        setSelectedGroup(group);
        setIsDetailsModalOpen(true);
    };

    const subTabs: TabItem<GroupsSubTab>[] = [
        { id: "password_groups", label: t("passwords.groups.tab") },
        { id: "user_groups", label: t("passwords.user_groups.tab") },
    ];

    const sortedGroups = React.useMemo(() => {
        let result = [...groups].sort((a, b) => a.name.localeCompare(b.name));
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter((g) => g.name.toLowerCase().includes(query));
        }
        return result;
    }, [groups, searchQuery]);

    const columns: TableColumn<PasswordGroup>[] = [
        {
            key: "name",
            label: t("passwords.form.group_name"),
            sortable: true,
        },
        {
            key: "updated_at",
            label: t("passwords.groups.updated_at"),
            sortable: true,
            render: (row) => (
                <div className="">
                    {row.updated_at ? formatDate(row.updated_at, false) : ""}
                </div>
            ),
        },
        {
            key: "shared_users",
            label: t("passwords.groups.share_with_users"),
            type: "multivalue",
            max: 0,
            multiValueExtractor: (row) =>
                row.shared_with_users?.map((u) => u.name) || [],
        },
        {
            key: "shared_groups",
            label: t("passwords.groups.share_with_user_groups"),
            type: "multivalue",
            max: 0,
            multiValueExtractor: (row) =>
                row.shared_with_password_user_groups?.map((g) => g.name) || [],
        },
    ];

    const actions: TableAction<PasswordGroup>[] = [
        {
            label: t("common.edit"),
            icon: <Icon value="edit" size={16} />,
            onClick: onEditGroup,
            show: (row) => row.user_id == user?.id,
        },
        {
            label: t("common.delete"),
            icon: <Icon value="delete" size={16} />,
            variant: "delete",
            onClick: onDeleteGroup,
            show: (row) => row.user_id == user?.id,
        },
    ];

    return (
        <div className="flex flex-col gap-4 w-full animate-fade-in">
            <Tabs<GroupsSubTab>
                tabs={subTabs}
                activeTab={subTab}
                onChange={onChangeSubTab}
                subtabs={true}
                syncUrl={false}
                full={false}
                //variant="pills"
            />

            {subTab === "password_groups" ? (
                <TableSimple
                    viewKey="vigepra_password_groups"
                    searchValue={searchQuery}
                    onSearchChange={onSearchChange}
                    appearance="workspace"
                    data={sortedGroups}
                    columns={columns}
                    actions={actions}
                    actionMenu={true}
                    searchable={true}
                    emptyText={t("passwords.groups.empty")}
                    onRowClick={openDetails}
                />
            ) : (
                <PasswordUserGroupsView
                    searchQuery={searchQuery}
                    onSearchChange={onSearchChange}
                    users={users}
                    triggerCreate={triggerCreateUserGroup}
                />
            )}

            <Modal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                title={t("passwords.groups.title")}
                footer={
                    <div className="flex items-center justify-center gap-2 w-full">
                        {selectedGroup && selectedGroup.user_id == user?.id && (
                            <>
                                <button
                                    className="btn warning"
                                    onClick={() => {
                                        setIsDetailsModalOpen(false);
                                        onEditGroup(selectedGroup);
                                    }}
                                >
                                    <Icon value="edit" size={16} />
                                    {t("common.edit")}
                                </button>
                                <button
                                    className="btn danger"
                                    onClick={() => {
                                        setIsDetailsModalOpen(false);
                                        onDeleteGroup(selectedGroup);
                                    }}
                                >
                                    <Icon value="delete" size={16} />
                                    {t("common.delete")}
                                </button>
                            </>
                        )}
                    </div>
                }
            >
                {selectedGroup && (
                    <div className="flex flex-col gap-4 pt-2 text-center">
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("common.name")}
                            </span>
                            <p className="text-sm text-slate-600 dark:text-zinc-400">
                                {selectedGroup.name}
                            </p>
                        </div>

                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("passwords.groups.updated_at")}
                            </span>
                            <p className="text-sm text-slate-600 dark:text-zinc-400">
                                {selectedGroup.updated_at
                                    ? formatDate(
                                          selectedGroup.updated_at,
                                          false
                                      )
                                    : "-"}
                            </p>
                        </div>

                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("passwords.groups.share_with_users")}
                            </span>
                            {(selectedGroup.shared_with_users?.length || 0) >
                            0 ? (
                                <div className="flex flex-wrap justify-center gap-2 mt-2">
                                    {selectedGroup.shared_with_users?.map(
                                        (u) => (
                                            <Badge key={u.id} variant="primary">
                                                {u.name}
                                            </Badge>
                                        )
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500 mt-1">-</p>
                            )}
                        </div>

                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("passwords.groups.share_with_user_groups")}
                            </span>
                            {(selectedGroup.shared_with_password_user_groups
                                ?.length || 0) > 0 ? (
                                <div className="flex flex-wrap justify-center gap-2 mt-2">
                                    {selectedGroup.shared_with_password_user_groups?.map(
                                        (p) => (
                                            <Badge key={p.id} variant="primary">
                                                {p.name}
                                            </Badge>
                                        )
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500 mt-1">-</p>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
